// // src/views/words-lists/slice/slice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchWordsListPage,
  fetchWordsList,
  createWordsList,
  deleteWordsList,
  duplicateWordsList,
  refreshWordsListPage,
  createWordsListInFolder,
} from './actions';

// Udostępnień list tu nie ma: okno „Udostępnij” wczytuje je przy otwarciu i trzyma u siebie
interface WordsListState {
  list: any[];
  lists: any[];
  currentList: any;
  currentPage: number;
  isLoading: boolean;
  isAdding: boolean;
  addingError: any;
  isDeleting: boolean;
  deletingError: any;
  error: string | null;
}

const initialState: WordsListState = {
  list: [],
  lists: [],
  currentList: null,
  currentPage: 1,
  isLoading: false,
  isAdding: false,
  addingError: null,
  isDeleting: false,
  deletingError: null,
  error: null,
};

const wordsListSlice = createSlice({
  name: 'wordsList',
  initialState,
  reducers: {
    // Przy wylogowaniu i logowaniu — listy poprzedniego konta nie mogą przejść na następne
    resetData: () => initialState,
    clearError: (state) => {
      state.error = null;
      state.addingError = null;
      state.deletingError = null;
    },
    setAddingWordsListError: (state, action: PayloadAction<any>) => {
      state.addingError = action.payload;
    },
    setDeletingWordsListError: (state, action: PayloadAction<any>) => {
      state.deletingError = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ==========================================
    // FETCH WORDS LIST PAGE
    // ==========================================
    builder.addCase(fetchWordsListPage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchWordsListPage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.list = action.payload.records;
      state.lists = action.payload.records;
    });
    builder.addCase(fetchWordsListPage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Błąd podczas pobierania list';
    });

    // ==========================================
    // REFRESH PAGE
    // ==========================================
    builder.addCase(refreshWordsListPage.fulfilled, (state, action) => {
      state.list = action.payload.records;
      state.lists = action.payload.records;
    });

    // ==========================================
    // FETCH SINGLE WORDS LIST
    // ==========================================
    builder.addCase(fetchWordsList.fulfilled, (state, action) => {
      state.currentList = action.payload;
    });

    // ==========================================
    // CREATE WORDS LIST
    // ==========================================
    builder.addCase(createWordsList.pending, (state) => {
      state.isAdding = true;
      state.addingError = null;
    });
    builder.addCase(createWordsList.fulfilled, (state, action) => {
      state.isAdding = false;
      state.list = [action.payload, ...state.list];
      state.lists = [action.payload, ...state.lists];
    });
    builder.addCase(createWordsList.rejected, (state, action) => {
      state.isAdding = false;
      state.addingError = action.error.message || 'Błąd podczas tworzenia listy';
    });

    // ==========================================
    // CREATE IN FOLDER
    // ==========================================
    builder.addCase(createWordsListInFolder.pending, (state) => {
      state.isAdding = true;
      state.addingError = null;
    });
    builder.addCase(createWordsListInFolder.fulfilled, (state, action) => {
      state.isAdding = false;
      state.list = [action.payload, ...state.list];
      state.lists = [action.payload, ...state.lists];
    });
    builder.addCase(createWordsListInFolder.rejected, (state, action) => {
      state.isAdding = false;
      state.addingError = action.error.message || 'Błąd podczas tworzenia listy';
    });

    // ==========================================
    // DELETE WORDS LIST
    // ==========================================
    builder.addCase(deleteWordsList.pending, (state) => {
      state.isDeleting = true;
      state.deletingError = null;
    });
    builder.addCase(deleteWordsList.fulfilled, (state, action) => {
      state.isDeleting = false;
      state.list = state.list.filter((l) => l.id !== action.payload);
      state.lists = state.lists.filter((l) => l.id !== action.payload);
    });
    builder.addCase(deleteWordsList.rejected, (state, action) => {
      state.isDeleting = false;
      state.deletingError = action.error.message || 'Błąd podczas usuwania listy';
    });

    // ==========================================
    // DUPLICATE WORDS LIST
    // ==========================================
    builder.addCase(duplicateWordsList.fulfilled, (state, action) => {
      state.list = [action.payload.copy, ...state.list];
      state.lists = [action.payload.copy, ...state.lists];
    });
  },
});

export const {
  resetData,
  clearError,
  setAddingWordsListError,
  setDeletingWordsListError,
} = wordsListSlice.actions;

export default {
  name: wordsListSlice.name,
  reducer: wordsListSlice.reducer,
  actions: wordsListSlice.actions,
};
